import { Button, Confirm, Modal } from 'semantic-ui-react'
import { Link, LinkProps } from 'react-router-dom'
import { AxiosError } from 'axios'
import { format } from 'date-fns'
import * as React from 'react'
import useSWR from 'swr'

import { classNames } from '../utils'
import { Collection } from '../types'
import { useToasts } from './toasts/ToastsProvider'
import { ENV_VARS } from '../env'
import { useApi } from '../store/mainContext'

type GenericEntity = {
  _id: string
  name: string
  email?: string
  createdOn: number
}

interface Props<T> {
  entityLinkLabel(entity: T): React.ReactNode
  entityLinkTo(entity: T): LinkProps['to']
  collection: Collection
  entityName?: string
  onMerge(ids: string[]): Promise<any>
  entity: T
  params: Record<string, any>
}

export default function DuplicatesCard<T extends GenericEntity>(
  props: Props<T>,
) {
  const {
    entityLinkLabel,
    entityLinkTo,
    entityName = 'entity',
    collection,
    onMerge,
    entity,
    params,
  } = props

  const { addToast } = useToasts()
  const api = useApi()

  const [entityToMerge, setEntityToMerge] = React.useState<T | null>(null)

  const [isMerging, setIsMerging] = React.useState(false)

  const dupes = useSWR(
    ['entity-duplicates', collection, ...Object.values(params)],
    () =>
      api
        .get<T[]>(`${collection}/any`, { params })
        .then((res) => res.data.filter((e) => e._id !== entity._id)),
  )

  const handleMerge = () => {
    if (!entityToMerge) return
    setIsMerging(true)
    return onMerge([entityToMerge._id, entity._id])
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .finally(() => {
        setEntityToMerge(null)
        setIsMerging(false)
      })
  }

  if ((dupes.data || []).length === 0) return null

  return (
    <>
      <div className="mx-9 mb-9 rounded-md border border-dotted border-[#e9c776] bg-[#fff8db] p-4 text-[#b58105]">
        <div className="mb-4 text-[15.4px] font-bold">
          This {entityName} could be duplicated:
        </div>

        {(dupes.data || []).map((dupeEntity, i) => (
          <div
            className={classNames(
              'flex items-center justify-between gap-2 border-b border-dotted border-[#e9c776] py-2',
              i === 0 && 'border-t',
            )}
            key={dupeEntity._id}
          >
            <Link
              target={
                ENV_VARS.REACT_APP_USE_NEW_TABS !== 'false'
                  ? '_blank'
                  : undefined
              }
              to={entityLinkTo(dupeEntity)}
            >
              {entityLinkLabel(dupeEntity)}
            </Link>

            <Button
              size="mini"
              color="red"
              onClick={() => setEntityToMerge(dupeEntity)}
              type="button"
              className="!mr-0"
            >
              Merge
            </Button>
          </div>
        ))}
      </div>

      <Confirm
        header={`Merge ${entityName}`}
        key={entityToMerge?._id}
        open={!!entityToMerge}
        content={
          <Modal.Content>
            <div className="-mt-6 text-base">
              You are about to merge these two entities:
            </div>

            <ul className="mt-6 space-y-2">
              <li className="rounded bg-slate-100 px-3 py-2">
                <div className="flex justify-between text-base">
                  <span className="font-semibold">{entity.name}</span>
                  {entity.email ? (
                    <span>{entity.email}</span>
                  ) : (
                    <span className="italic text-slate-600">
                      E-mail not provided
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  Created on <strong>{format(entity.createdOn, 'P')}</strong>
                </div>
              </li>

              {entityToMerge && (
                <li className="rounded bg-slate-100 px-3 py-2">
                  <div className="flex justify-between text-base">
                    <span className="font-semibold">{entityToMerge.name}</span>
                    <span>{entityToMerge.email}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Created on{' '}
                    <strong>{format(entityToMerge.createdOn, 'P')}</strong>
                  </div>
                </li>
              )}
            </ul>

            <div className="mt-6 text-base">This action will:</div>

            <ul className="mt-4 list-decimal space-y-2 pl-5">
              <li>
                Move all tasks and events from the <b>newest</b> {entityName} to
                the <strong>oldest</strong> {entityName}
              </li>
              <li>
                <strong className="text-red-600">Remove</strong> the{' '}
                <strong>newest</strong> {entityName}.
              </li>
            </ul>

            <div className="mt-6 text-base">
              <strong>This action cannot be undone.</strong> Do you want to
              continue?
            </div>
          </Modal.Content>
        }
        onCancel={() => setEntityToMerge(null)}
        closeOnDimmerClick={!isMerging}
        closeOnEscape={!isMerging}
        size="tiny"
        onConfirm={handleMerge}
        confirmButton={
          <Button loading={isMerging} disabled={isMerging} negative>
            Yeap... let&apos;s merge them
          </Button>
        }
        cancelButton={
          <Button basic disabled={isMerging}>
            Ugh... no, no yet
          </Button>
        }
      />
    </>
  )
}
